import { ChevronDown, Download, Plus, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCampaigns } from '@/hooks/useCampaigns';

import { BOOKING_STATUS_LABELS, BOOKING_STATUSES } from '@repo/shared-types';

export type ExportPreset = 'all' | 'today' | 'week' | 'month' | 'past' | 'future';

interface BookingFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  campaignId: string;
  onCampaignChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  onAddClick: () => void;
  onExportClick: (preset: ExportPreset) => void;
}

export const BookingFilters = ({
  search,
  onSearchChange,
  campaignId,
  onCampaignChange,
  status,
  onStatusChange,
  onAddClick,
  onExportClick,
}: BookingFiltersProps) => {
  const { data: campaignsData } = useCampaigns();
  const campaigns = campaignsData?.data ?? campaignsData ?? [];

  return (
    <div className="border-border/40 px-6 py-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-xl font-semibold tracking-tight">Bookings</h1>
        <div className="flex items-center gap-2">
          <Button onClick={onAddClick} size="sm" className="gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            New Booking
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                <Download className="h-3.5 w-3.5" />
                Export
                <ChevronDown className="h-3 w-3 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onExportClick('all')}>All Bookings</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExportClick('today')}>Today</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExportClick('week')}>This Week</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExportClick('month')}>This Month</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExportClick('future')}>
                Future Only
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExportClick('past')}>Past Only</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="text-muted-foreground/60 pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2" />
          <Input
            placeholder="Search by title, name, or phone..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="h-8 w-72 pl-8 text-sm"
          />
        </div>
        <Select value={campaignId} onValueChange={onCampaignChange}>
          <SelectTrigger className="h-8 w-44 text-sm">
            <SelectValue placeholder="All Campaigns" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Campaigns</SelectItem>
            {(Array.isArray(campaigns) ? campaigns : []).map((c: { id: string; name: string }) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={onStatusChange}>
          <SelectTrigger className="h-8 w-32 text-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {BOOKING_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {BOOKING_STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
